import { Layers} from 'lucide-react';
import type { ConditionGroup, LogicalOperator, OperatorType, RuleStructure } from './nestedRuleFunctions';
import RuleCondition from './RuleCondition';
import DropdownSelect from './DropdownSelect';
import { Attribute, DataTypeItem } from '@/shared/partnerschema';

interface RuleGroupProps {
    group: ConditionGroup;
    level: number;
    groupIndex?: number;
    rule: RuleStructure;
    dataType: DataTypeItem[];
    attribute: Attribute[];
    operators: OperatorType[];
    onAddCondition: (groupId: string) => void;
    onAddSubgroup: (groupId: string) => void;
    onUpdateCondition: (condId: string, field: string, value: any) => void;
    onUpdateDatatype: (condId: string, field: string, value: any) => void;
    onUpdateLogicalOperator: (groupId: string, index: number, operator: LogicalOperator) => void;
    onUpdateGroupOperator: (index: number, operator: LogicalOperator) => void;
    onDeleteCondition: (groupId: string, condId: string) => void;
    onDeleteGroup?: (groupId: string) => void;
    isFirstGroup?: boolean;
    version: boolean;
}

const RuleGroup = ({
    group,
    level,
    groupIndex,
    rule,
    dataType,
    attribute,
    operators,
    onAddCondition,
    onAddSubgroup,
    onUpdateCondition,
    onUpdateDatatype,
    onUpdateLogicalOperator,
    onUpdateGroupOperator,
    onDeleteCondition,
    onDeleteGroup,
    version
}: RuleGroupProps) => {
    const bgColor = level === 0 ? 'bg-gray-50' : 'bg-blue-50';
    const borderColor = level === 0 ? 'border-gray-300' : 'border-blue-300';
    return (
        <div className="space-y-2">

            {level === 0 && groupIndex !== undefined && groupIndex > 0 && (
                <div className="flex items-center gap-2">
                    <DropdownSelect
                        value={rule.groupOperators[groupIndex - 1] || 'AND'}
                        onValueChange={(value) => {
                            onUpdateGroupOperator(groupIndex - 1, value as LogicalOperator)
                        }}
                        disabled
                        options={[{ value: "AND", label: "AND" }, { value: "OR", label: "OR" }]}
                    />
                </div>
            )}

            <div className={`${bgColor} border-2 ${borderColor} rounded-lg py-2 px-4 space-y-1`}>

                <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold flex items-center gap-2">
                        {level === 0 ? <Layers className="w-5 h-5" /> : null}
                        {group.type === 'group'
                            ? `Group ${groupIndex !== undefined ? groupIndex + 1 : ''}`
                            : 'Subgroup'}
                    </h3>
                    {/* {group.id !== "group-1" && onDeleteGroup && (
                        <button
                            onClick={() => onDeleteGroup(group.id)}
                            className="p-1.5 hover:bg-red-50 rounded text-red-500"
                            title={`Delete ${group.type === 'group' ? 'Group' : 'Subgroup'}`}
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )} */}
                </div>

                {group.conditions.map((item, index) => {
                    if ('conditions' in item) {
                        return (
                            <div key={item.id} className="ml-6">
                                {index > 0 && (
                                    <div className="flex items-center gap-2 mb-2">
                                        <DropdownSelect
                                            value={group.logicalOperators[index - 1] || 'AND'}
                                            onValueChange={(value) => {
                                                onUpdateLogicalOperator(group.id, index - 1, value as LogicalOperator)
                                            }}
                                            disabled
                                            options={[{ value: "AND", label: "AND" }, { value: "OR", label: "OR" }]}
                                        />
                                    </div>
                                )}
                                <RuleGroup
                                    group={item}
                                    level={level + 1}
                                    rule={rule}
                                    dataType={dataType}
                                    attribute={attribute}
                                    operators={operators}
                                    onAddCondition={onAddCondition}
                                    onAddSubgroup={onAddSubgroup}
                                    onUpdateCondition={onUpdateCondition}
                                    onUpdateDatatype={onUpdateDatatype}
                                    onUpdateLogicalOperator={onUpdateLogicalOperator}
                                    onUpdateGroupOperator={onUpdateGroupOperator}
                                    onDeleteCondition={onDeleteCondition}
                                    onDeleteGroup={onDeleteGroup}
                                    isFirstGroup={false}
                                    version={version}
                                />
                            </div>
                        );
                    } else {
                        return (
                            <RuleCondition
                                key={item.id}
                                condition={item}
                                groupId={group.id}
                                index={index}
                                groupOperators={group.logicalOperators}
                                isFirstInGroup={index === 0}
                                dataType={dataType}
                                operators={operators}
                                onUpdateCondition={onUpdateCondition}
                                onUpdateDatatype={onUpdateDatatype}
                                onUpdateLogicalOperator={onUpdateLogicalOperator}
                                onDeleteCondition={onDeleteCondition}
                                version={version}
                            />
                        );
                    }
                })}
            </div>
        </div>
    );
};

export default RuleGroup;